import { useCallback, useMemo, useState } from 'react';

import type { TipTapDocument, TipTapVariable } from '@poseidon/components/Input2/TipTapInput';

import type { SequenceContentOverrides, SequenceStep } from '~/components/Sequence/sequenceSteps';
import type { SequenceContactStepStatus } from '~/components/Sequence/sequenceStepStatus';
import type { SequenceTimelineContextValue } from '~/components/Sequence/sequenceTimelineContext';
import { SequenceCurrentStepMarker } from '~/components/Sequence/SequenceCurrentStepMarker';
import { SequencePausedNotice } from '~/components/Sequence/SequencePausedNotice';
import {
  isPrepNode,
  manualTaskWaitNodeForStepType,
  nodeStatusLabel,
  withContentOverrides,
} from '~/components/Sequence/sequenceSteps';
import { contactStepStatus, sequenceStepVisual } from '~/components/Sequence/sequenceStepStatus';
import { sequenceTimelineBlocks } from '~/components/Sequence/sequenceTimeline';
import { SequenceTimelineBlocks } from '~/components/Sequence/SequenceTimelineBlocks';
import { SequenceTimelineContext } from '~/components/Sequence/sequenceTimelineContext';

interface Props {
  /** The sequence's authored steps (the template). */
  steps: SequenceStep[];
  /** This contact's own text per step, keyed by override key. */
  overrides?: SequenceContentOverrides;
  /** What became of each step for this contact, keyed by override key. */
  stepStatus?: Record<string, SequenceContactStepStatus>;
  /** The journey node the contact rests on right now. */
  currentNodeId: string | null;
  /** `@`-variables + `/`-documents for the inline editor (this contact's values). */
  variables?: TipTapVariable[];
  documents?: TipTapDocument[];
  /** Persist this contact's subject / body for one step (keyed by override key). */
  onSaveStepContent: (overrideKey: string, next: { body?: string; subject?: string }) => Promise<void>;
  /** Complete the call / reminder to-do the contact is parked on. */
  onCompleteManualStep: (nodeId: string, stepKey: string) => void;
  /** "Today" for the day cards' dates — tests pin it; the page uses the real clock. */
  now?: Date;
  /** A rep paused this contact's enrollment: every step holds where it is until it's resumed. */
  paused?: boolean;
  /** The sequence's name, heading the steps — set when the contact is in several, so each reads as its own. */
  title?: string;
}

/**
 * One contact's sequence drawn the way the contact page draws a campaign: day cards, wait pills, and the
 * "did they accept?" card with the not-accepted steps nested inside — built from the campaign's own
 * components, so the two read (and behave) the same. What has already run, or never will, drops out as it
 * does for a campaign. Text edits go to this contact's copy only; the sequence template is never touched.
 */
export function ContactSequenceTimeline({
  steps,
  overrides,
  stepStatus,
  currentNodeId,
  variables,
  documents,
  onSaveStepContent,
  onCompleteManualStep,
  now,
  paused = false,
  title,
}: Props) {
  // This contact's text on every line — what the engine validates and sends.
  const contentSteps = useMemo(() => withContentOverrides(steps, overrides ?? {}), [steps, overrides]);
  const inPrep = currentNodeId != null && isPrepNode(currentNodeId);
  const statusAt = useCallback((key: string) => contactStepStatus(stepStatus?.[key], inPrep), [stepStatus, inPrep]);
  const blocks = useMemo(
    () => sequenceTimelineBlocks(contentSteps, [], now ?? new Date(), { statusAt, overrides: overrides ?? {} }),
    [contentSteps, now, statusAt, overrides],
  );

  const [editingKey, setEditingKey] = useState<string | null>(null);
  const timeline = useMemo<SequenceTimelineContextValue>(
    () => ({
      editingKey,
      onEdit: setEditingKey,
      onCancelEdit: () => setEditingKey(null),
      onSave: async (key, data) => {
        await onSaveStepContent(key, { body: data.body ?? '', subject: data.subject ?? undefined });
        setEditingKey(null);
      },
      onMarkDone: (key, type) => {
        const waitNode = type === 'reminder' || type === 'call' ? manualTaskWaitNodeForStepType(type) : null;
        if (waitNode != null) onCompleteManualStep(waitNode, key);
      },
      variables,
      documents,
    }),
    [editingKey, onSaveStepContent, onCompleteManualStep, variables, documents],
  );
  // Still in the automatic front-half (research → qualify → enroll): say which stage, in the step tones.
  const prepVisual =
    inPrep && currentNodeId != null ? sequenceStepVisual({ state: 'current', nodeId: currentNodeId }) : null;

  return (
    <SequenceTimelineContext.Provider value={timeline}>
      <div data-sequence-timeline className="flex flex-col gap-3">
        {title != null && (
          <h3 data-sequence-title className="text-sm font-semibold text-lynch-700 dark:text-lynch-200">
            {title}
          </h3>
        )}
        {paused && <SequencePausedNotice />}
        {prepVisual != null && currentNodeId != null && (
          <div data-sequence-prep className="flex items-center gap-2 text-xs text-lynch-500 dark:text-lynch-400">
            Getting ready
            <SequenceCurrentStepMarker tone={prepVisual.tone} stageLabel={nodeStatusLabel(currentNodeId)} />
          </div>
        )}
        {blocks.length === 0 && (
          <p className="text-sm text-lynch-500 dark:text-lynch-400">Nothing left to send — every step has run.</p>
        )}
        <SequenceTimelineBlocks blocks={blocks} />
      </div>
    </SequenceTimelineContext.Provider>
  );
}
